// src/components/DeleteCollectionModal.jsx
import React, { useEffect, useState } from "react";

/**
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - collectionId: string
 * - clientId: string  (user.id du collectionneur)
 * - supabase: SupabaseClient (instance déjà initialisée dans ton app)
 * - onDeleted?: () => void (callback après suppression OK)
 */
export default function DeleteCollectionModal({
  open,
  onClose,
  collectionId,
  clientId,
  supabase,
  onDeleted,
}) {
  const [loading, setLoading] = useState(false);
  const [deleteMode, setDeleteMode] = useState("reassign_unclassified"); // 'reassign_pick' | 'reassign_unclassified' | 'delete_all'
  const [otherCollections, setOtherCollections] = useState([]);
  const [targetCollectionId, setTargetCollectionId] = useState("");
  const [artworkCount, setArtworkCount] = useState(0);
  const [collectionTitle, setCollectionTitle] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState("");

  const dangerous = deleteMode === "delete_all";

  useEffect(() => {
    if (!open) return;
    setError("");
    setConfirmText("");
    setLoading(true);

    const load = async () => {
      // 1) nom + count
      const [{ data: col }, { count },] = await Promise.all([
        supabase.from("collections").select("title").eq("id", collectionId).maybeSingle(),
        supabase.from("artworks").select("id", { head: true, count: "exact" })
          .eq("client_id", clientId).eq("collection_id", collectionId),
        supabase.from("collections") // prefetch other list (we also do below)
      ]);

      setCollectionTitle(col?.title || "Collection");
      setArtworkCount(count || 0);

      // 2) Autres collections
      const { data: others } = await supabase
        .from("collections")
        .select("id,title")
        .eq("client_id", clientId)
        .neq("id", collectionId)
        .order("title", { ascending: true });

      setOtherCollections(others || []);
      setLoading(false);
    };

    load().catch((e) => {
      setError(e.message || "Erreur de chargement");
      setLoading(false);
    });
  }, [open, collectionId, clientId, supabase]);

  async function getOrCreateUnclassifiedCollection() {
    const { data: found } = await supabase
      .from("collections")
      .select("id")
      .eq("client_id", clientId)
      .eq("title", "À classer")
      .maybeSingle();
    if (found?.id) return found.id;

    const { data: created, error } = await supabase
      .from("collections")
      .insert({ title: "À classer", description: "Réassignations automatiques", client_id: clientId })
      .select("id")
      .single();
    if (error) throw error;
    return created.id;
  }

  async function handleDelete() {
    try {
      setError("");
      setLoading(true);

      // Si aucune œuvre, suppression directe
      if (artworkCount === 0) {
        const { error: delErr } = await supabase
          .from("collections").delete().eq("id", collectionId).eq("client_id", clientId);
        if (delErr) throw delErr;
        setLoading(false);
        onClose();
        onDeleted && onDeleted();
        return;
      }

      if (deleteMode === "delete_all") {
        // Supprimer œuvres puis collection
        const { error: delArts } = await supabase
          .from("artworks").delete()
          .eq("collection_id", collectionId).eq("client_id", clientId);
        if (delArts) throw delArts;

        const { error: delCol } = await supabase
          .from("collections").delete().eq("id", collectionId).eq("client_id", clientId);
        if (delCol) throw delCol;

        setLoading(false);
        onClose();
        onDeleted && onDeleted();
        return;
      }

      // Réassignation vers cible
      let targetId = targetCollectionId;
      if (deleteMode === "reassign_unclassified") {
        targetId = await getOrCreateUnclassifiedCollection();
      }
      if (!targetId) {
        setError("Choisis une collection de destination.");
        setLoading(false);
        return;
      }

      // Update œuvres -> target, puis suppression collection
      const { error: upErr } = await supabase
        .from("artworks")
        .update({ collection_id: targetId })
        .eq("collection_id", collectionId)
        .eq("client_id", clientId);
      if (upErr) throw upErr;

      const { error: delCol2 } = await supabase
        .from("collections").delete().eq("id", collectionId).eq("client_id", clientId);
      if (delCol2) throw delCol2;

      setLoading(false);
      onClose();
      onDeleted && onDeleted();
    } catch (e) {
      setLoading(false);
      setError(e.message || "Erreur pendant la suppression");
    }
  }

  const disableConfirm =
    loading ||
    (dangerous && confirmText.trim().toUpperCase() !== "SUPPRIMER") ||
    (deleteMode === "reassign_pick" && !targetCollectionId);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
        <h3 className="text-xl font-semibold mb-1">Supprimer la collection</h3>
        <p className="text-sm text-gray-600 mb-4">
          <b>{collectionTitle}</b> — {artworkCount} œuvre{artworkCount>1?"s":""}
        </p>

        <div className="space-y-3">
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="delmode"
              checked={deleteMode === "reassign_unclassified"}
              onChange={() => setDeleteMode("reassign_unclassified")}
              disabled={loading}
            />
            <span>
              Réassigner toutes les œuvres vers <b>“À classer”</b> (créée si besoin).
            </span>
          </label>

          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="delmode"
              checked={deleteMode === "reassign_pick"}
              onChange={() => setDeleteMode("reassign_pick")}
              disabled={loading}
            />
            <span>Réassigner vers une autre collection :</span>
          </label>

          {deleteMode === "reassign_pick" && (
            <select
              className="w-full border rounded px-2 py-1"
              value={targetCollectionId}
              onChange={(e) => setTargetCollectionId(e.target.value)}
              disabled={loading}
            >
              <option value="">— Choisir une collection —</option>
              {otherCollections.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
          )}

          <label className="flex items-start gap-2 text-red-700">
            <input
              type="radio"
              name="delmode"
              checked={deleteMode === "delete_all"}
              onChange={() => setDeleteMode("delete_all")}
              disabled={loading}
            />
            <span>
              <b>Supprimer définitivement</b> la collection <u>et toutes ses œuvres</u> (irréversible).
            </span>
          </label>

          {dangerous && (
            <div className="mt-2">
              <label className="block text-sm text-red-700 mb-1">
                Pour confirmer, tape <b>SUPPRIMER</b> :
              </label>
              <input
                className="w-full border rounded px-2 py-1"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="SUPPRIMER"
                disabled={loading}
              />
            </div>
          )}

          {error && <div className="text-red-600 text-sm">{error}</div>}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button className="btn-artsy btn-secondary-artsy" onClick={onClose} disabled={loading}>
            Annuler
          </button>
          <button
            className={`btn-artsy ${dangerous ? "btn-danger-artsy" : "btn-primary-artsy"}`}
            onClick={handleDelete}
            disabled={disableConfirm}
            title={dangerous ? "Cette action est irréversible" : ""}
          >
            {loading ? "Traitement…" : "Confirmer la suppression"}
          </button>
        </div>
      </div>
    </div>
  );
}
